import Tooltip from '@material-ui/core/Tooltip';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import ModalContainer from '@components//modals/ModalContainer';
import { toastError } from '@components/Toast';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import designUploadService from '@services/designUploadService';

import { getFileType, getIconByFileType, getShortName, isValidFile } from '@utils/Util';

export const VALID_FILE_SIZE = 26214400; // in binary 25mb

const UploadStyles = ({ collectionDetails, onClose, uploadType, onNewDesignModal, isReload = false }) => {
	const [fileLists, setFileLists] = useState([]);
	const [failureList, setFailureList] = useState([]);
	const [latestResponse, setLatestResponse] = useState(null);
	const isGlobalLoading = useSelector((store) => store.loaderStore);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const onSetNewUploads = (design, designResponse, techpackResponse, otherTechpackPageDocIdList) => {
		setFileLists((prev) => {
			let tmp = [...prev];
			let targetData = prev.find((obj) => obj.name === design.name);
			if (targetData) {
				targetData.isUploading = false;
				targetData.id = designResponse.id;
				targetData.docUrl = designResponse.docUrl;
				targetData.techpackId = techpackResponse?.id || null;
				targetData.otherTechpackPageDocIdList = otherTechpackPageDocIdList || [];
			}
			return tmp;
		});
	};

	const onSetNewUploadsFailure = (design) => {
		setFailureList((prev) => [...prev, design]);
	};

	const onRemoveDesign = (designId, type = 'MULTI') => {
		if (type === 'SINGLE') {
			const updatedData = { ...latestResponse };
			let item = updatedData?.otherDocResponseList.filter((obj) => obj.id !== designId);
			updatedData['otherDocResponseList'] = item;
			setLatestResponse(updatedData);
		}
		setFileLists((prev) => prev.filter((obj) => obj.id !== designId));
	};

	const onUploadMultiSyle = async (design) => {
		const response = await designUploadService.onUploadInMultiSyle(design);
		if (response === 'FAILURE') {
			onSetNewUploadsFailure(design);
		} else {
			if (response && response?.productDesignDocResponse) {
				onSetNewUploads(
					design,
					response?.productDesignDocResponse,
					response?.techpackDocResponse,
					response?.otherTechpackPageDocIdList
				);
			}
		}
	};

	const onUploadDesign = async (e, docType = 'PRODUCT_DESIGN') => {
		let allFileList = [];
		let files = [];
		files = [...e.target.files];
		files = [
			...files.filter((selected) => !fileLists.some((uploaded) => selected.name === uploaded.name))
		];
		files.length !== 0 &&
			(await files?.forEach((file) => {
				if (file) {
					if (isValidFile(file, uploadType === 'SINGLE' ? 'ALL' : 'imageAndPdf', VALID_FILE_SIZE)) {
						let data = {
							name: file?.name,
							docMimeType: file?.type,
							documentType: docType,
							docUrl: ''
						};
						let reader = new FileReader();
						reader.readAsDataURL(file);
						reader.onload = () => {
							data.docUrl = reader.result;
							setFileLists((prev) => [...prev, { ...data, isUploading: true }]);
							if (uploadType === 'MULTIPLE') {
								onUploadMultiSyle(data);
							} else {
								allFileList.push(data);
							}
						};
						reader.onerror = function (error) {
							toastError(error);
						};
					}
				}
			}));
		if (uploadType === 'SINGLE') {
			dispatch(openLoader());
			setTimeout(async () => {
				const response = await designUploadService.onUploadSingleDesign(allFileList, latestResponse);
				if (response !== 'FAILURE') {
					setLatestResponse(response);
					onSetNewSingleUploads(response);
				} else setFileLists([]);
				dispatch(closeLoader());
			}, 100);
		}
	};

	const onSetNewSingleUploads = (response) => {
		const { productDesignDocResponse, otherDocResponseList } = response;
		let newList = [productDesignDocResponse, ...otherDocResponseList];
		setFileLists(newList);
	};

	const onDisableButton = () => {
		let isDisable = true;
		fileLists.forEach((item) => {
			if (item.isUploading) {
				isDisable = true;
			} else {
				isDisable = false;
			}
		});
		if (isDisable) {
			return true;
		} else {
			return false;
		}
	};

	const isSubmitDisabled = () => {
		if (uploadType === 'SINGLE') {
			return fileLists?.length === 0 || isGlobalLoading;
		} else return onDisableButton();
	};

	const getDesignListsIds = () => {
		if (uploadType !== 'SINGLE') {
			return fileLists.map((item) => ({
				productDesignId: item?.id,
				techpackId: item?.techpackId
			}));
		} else {
			return [
				{
					productDesignId: latestResponse?.productDesignDocResponse?.id,
					techpackId: latestResponse?.techpackDocResponse?.id,
					otherDocIdList: latestResponse?.otherDocResponseList?.map((item) => item.id)
				}
			];
		}
	};

	const isRequestFromSuggestedBuyer = () => {
		let paramList = new URLSearchParams(location.search);
		const requestFrom = paramList.get('status');
		return requestFrom === 'SUGGESTED_FOR_BUYER';
	};

	const onSubmitStyles = async () => {
		dispatch(openLoader());
		const collectionId = collectionDetails?.id;
		const response = await designUploadService.onSubmitStyles(collectionId, getDesignListsIds());
		if (response.success) {
			onAddSuccess(collectionId);
		}
		dispatch(closeLoader());
	};

	const onAddSuccess = (collectionId) => {
		onAddDesign();
		if (location.pathname === `/collections/view/${collectionId}` && isReload) {
			return navigate(0);
		}
		return navigate(
			`/collections/view/${collectionId}/${
				isRequestFromSuggestedBuyer() ? '?status=SUGGESTED_FOR_BUYER' : ''
			}`
		);
	};

	const onAddDesign = () => {
		if (onNewDesignModal) {
			onNewDesignModal();
		}
	};

	const renderSingleDesign = (design) => {
		const featureImageId = latestResponse?.productDesignDocResponse?.id;
		return (
			<div className="action-icon">
				<>
					{!isGlobalLoading && (
						<img
							className="uploaded"
							src={`${process.env.PUBLIC_URL}/icons/check24.svg`}
							alt="check"
						/>
					)}
					{featureImageId !== design.id && (
						<img
							onClick={() => onRemoveDesign(design.id, 'SINGLE')}
							className="remove"
							src={`${process.env.PUBLIC_URL}/icons/Close-unchecked.svg`}
							alt="close"
						/>
					)}
				</>
			</div>
		);
	};

	const isDesignUploadFailed = (item) => failureList.some((design) => design?.name === item?.name);

	const renderMultiDesign = (item) => {
		return (
			<>
				<div className="action-icon">
					{item?.isUploading ? (
						<div className="spinner-border" role="status">
							<span className="sr-only"></span>
						</div>
					) : isDesignUploadFailed() ? (
						<>
							<img src={`${process.env.PUBLIC_URL}/icons/load.svg`} alt="load" />
							<img
								onClick={() => onRemoveDesign(item.id)}
								src={`${process.env.PUBLIC_URL}/icons/Close-unchecked.svg`}
								alt
							/>
						</>
					) : (
						<>
							<img
								className="uploaded"
								src={`${process.env.PUBLIC_URL}/icons/check24.svg`}
								alt="check"
							/>
							<img
								onClick={() => onRemoveDesign(item.id)}
								className="remove"
								src={`${process.env.PUBLIC_URL}/icons/Close-unchecked.svg`}
								alt="close"
							/>
						</>
					)}
				</div>
				{isDesignUploadFailed() && (
					<div className="upload-failed bg-white px-2 py-1 text-center">
						<p className="regular-12 red">Upload failed, please try again</p>
					</div>
				)}
			</>
		);
	};

	const renderTitle = () => (uploadType === 'SINGLE' ? 'single style' : 'multiple styles');

	const renderSingleImage = (item) => {
		return ['IMAGE'].includes(getFileType(item.name))
			? item.docUrl
			: getIconByFileType(getFileType(item.name));
	};

	const renderMultiImage = (item) => {
		const imageUrl = item?.isUploading ? renderSingleImage(item) : item.docUrl;
		return imageUrl;
	};

	const renderDesign = () => (
		<div className="common-popup  shadow-24dp upload-popup-modal">
			<div className="common-popup-header d-flex justify-content-between">
				<div className="popup-tilte">
					<h3 className="semibold-16 mb-0">Upload {renderTitle()}</h3>
				</div>
				<div className="close-btn" onClick={() => onClose()}>
					<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
				</div>
			</div>
			<div className="common-popup-body">
				<div className="upload-styles-container">
					<div className="uploaded-items scroll-y-label">
						<div
							className={
								uploadType === 'SINGLE'
									? 'single-style upload'
									: 'single-style upload multiple-style'
							}
						>
							<div className="file-uploader">
								<label htmlFor="drag-upload" className="drag-upload" />
								<input
									type="file"
									className="file-upload d-none"
									name="drag-upload"
									id="drag-upload"
									onChange={(e) => onUploadDesign(e)}
									onClick={(e) => (e.target.value = null)}
									multiple
									accept={`${uploadType === 'SINGLE' ? '*' : 'application/pdf, image/*'}`}
								/>
							</div>
						</div>

						{fileLists?.map((item, idx) => {
							return (
								<div className="single-style" key={item?.id}>
									<div className="image-container">
										<>
											<img
												alt="style"
												className="style-photo"
												src={
													uploadType === 'SINGLE'
														? renderSingleImage(item)
														: renderMultiImage(item)
												}
											/>

											{uploadType === 'SINGLE'
												? renderSingleDesign(item)
												: renderMultiDesign(item)}
										</>
										{uploadType !== 'SINGLE' && (
											<span className="number-tag regular-12 gray_dark_01">{`Style ${
												idx + 1
											}`}</span>
										)}
									</div>
									<Tooltip title={item?.name} placement="top" arrow>
										<p className="regular-12 gray_dark_02 mt-2">
											{getShortName(item?.name, 17)}
										</p>
									</Tooltip>
								</div>
							);
						})}
					</div>
				</div>
			</div>
			<div className="modal-footer">
				<div className="footer-buttons text-right">
					<button onClick={onClose} className="button gray-outline size32">
						Cancel
					</button>
					<button
						disabled={isSubmitDisabled() || isGlobalLoading}
						onClick={onSubmitStyles}
						className="button size32 ml-2"
					>
						Submit
					</button>
				</div>
			</div>
		</div>
	);
	return (
		<div>
			<ModalContainer onCloseModal={onClose} className="upload-style-modal">
				{renderDesign()}
			</ModalContainer>
		</div>
	);
};

export default UploadStyles;
